<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

// `app` is the Bitrix entry from `integrations/getAppIntegrations` (id, name, hooks,
// allow_multiple_hooks, ...) — see InboxIntegrationsPage.vue's `bitrixApp`.
const props = defineProps({
  app: {
    type: Object,
    required: true,
  },
  inbox: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['select', 'create', 'close']);

const { t } = useI18n();

const dialogRef = ref(null);

const hooks = computed(() => props.app?.hooks || []);

const hookName = hook =>
  hook.settings?.hook_name ||
  t('INBOX_MGMT.INBOX_INTEGRATIONS.BITRIX_PICKER.UNNAMED');

const linkStatusLabel = hook => {
  const linkedInboxId = hook.settings?.linked_inbox_id;
  if (!linkedInboxId) {
    return t('INBOX_MGMT.INBOX_INTEGRATIONS.BITRIX_PICKER.NOT_LINKED');
  }
  if (Number(linkedInboxId) === Number(props.inbox.id)) {
    return t('INBOX_MGMT.INBOX_INTEGRATIONS.BITRIX_PICKER.LINKED_HERE');
  }
  return t('INBOX_MGMT.INBOX_INTEGRATIONS.BITRIX_PICKER.LINKED_ELSEWHERE');
};

const headers = computed(() => [
  t('INBOX_MGMT.INBOX_INTEGRATIONS.BITRIX_PICKER.TABLE.NAME'),
  t('INBOX_MGMT.INBOX_INTEGRATIONS.BITRIX_PICKER.TABLE.STATUS'),
  '',
]);

const dialogTitle = computed(() =>
  t('INBOX_MGMT.INBOX_INTEGRATIONS.BITRIX_PICKER.TITLE')
);
const dialogDescription = computed(() =>
  t('INBOX_MGMT.INBOX_INTEGRATIONS.BITRIX_PICKER.DESCRIPTION')
);
const selectButtonLabel = computed(() =>
  t('INBOX_MGMT.INBOX_INTEGRATIONS.BITRIX_PICKER.SELECT_BUTTON')
);
const createButtonLabel = computed(() =>
  t('INBOX_MGMT.INBOX_INTEGRATIONS.BITRIX_PICKER.CREATE_BUTTON')
);
const emptyStateLabel = computed(() =>
  t('INBOX_MGMT.INBOX_INTEGRATIONS.BITRIX_PICKER.EMPTY')
);

const selectHook = hook => {
  emit('select', hook);
  dialogRef.value?.close();
};

const createNew = () => {
  emit('create');
  dialogRef.value?.close();
};

const handleClose = () => emit('close');

const open = () => dialogRef.value?.open();
const close = () => dialogRef.value?.close();

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="dialogTitle"
    :description="dialogDescription"
    :show-cancel-button="false"
    :show-confirm-button="false"
    width="lg"
    @close="handleClose"
  >
    <div class="flex flex-col gap-4">
      <BaseTable
        v-if="hooks.length"
        :headers="headers"
        :items="hooks"
        class="max-h-80 overflow-y-auto"
      >
        <template #row="{ items }">
          <BaseTableRow v-for="hook in items" :key="hook.id" :item="hook">
            <template #default>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-12 font-medium">
                  {{ hookName(hook) }}
                </span>
              </BaseTableCell>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ linkStatusLabel(hook) }}
                </span>
              </BaseTableCell>
              <BaseTableCell align="end">
                <NextButton
                  sm
                  faded
                  slate
                  type="button"
                  :label="selectButtonLabel"
                  @click="selectHook(hook)"
                />
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
      <p v-else class="text-sm text-n-slate-11">
        {{ emptyStateLabel }}
      </p>

      <NextButton
        v-if="app.allow_multiple_hooks"
        type="button"
        icon="i-lucide-plus"
        :label="createButtonLabel"
        @click="createNew"
      />
    </div>
    <template #footer />
  </Dialog>
</template>
